<script lang="ts">
import { defineComponent, ref, shallowRef, type PropType, inject } from 'vue';
import type { CustomOverlayItem, Instance, Point } from 'pspdfkit';
import Moveable from 'moveable';
import type { Able, OnDrag, OnResize } from 'moveable';
import {
    providePdfViewerKey,
    provideTransformScreenToPageSpaceKey,
    providePSPDFKit,
    type InitialMarking,
} from '@/components/PdfViewer/PdfViewer';
import type { Point2D } from '@/types/objects';

type Size = { width: number; height: number };
type PressEvent = { pageIndex: number; point: Point; nativeEvent: Event };

export default defineComponent({
    name: 'MoveableMarking',
    inheritAttrs: false,
    props: {
        /**
         * Data of the marking to be displayed
         */
        marking: {
            type: [Object, null] as PropType<InitialMarking | null>,
            default: null,
        },
    },
    emits: [
        'click-cta',
        'update:marking',
    ],
    setup() {
        const PSPDFKit = inject(providePSPDFKit);
        if (!PSPDFKit) throw new Error('PSPDFKit is not provided');

        return {
            PSPDFKit,
            instance: inject(providePdfViewerKey),
            transformScreenToPageSpace: inject(provideTransformScreenToPageSpaceKey),

            isResizing: ref(false),
            isDragging: ref(false),
            isTouchmove: ref(false),
            moveable: shallowRef<Moveable | undefined>(undefined),
            overlayItem: shallowRef<CustomOverlayItem | undefined>(undefined),
        };
    },
    computed: {
        overlayId() {
            return `moveable-marking-${this.$id}`;
        },
    },
    watch: {
        instance: {
            immediate: true,
            handler(val, prev) {
                if (prev) {
                    this.cleanup(prev);
                }

                if (val) this.init(val);
            },
        },
        marking(marking: InitialMarking) {
            if (!marking) {
                this.reset();
                return;
            }

            if (this.overlayItem?.pageIndex !== marking.page - 1) {
                this.reset();
            }

            if (!this.overlayItem) {
                this.create(marking.page, { x: marking.xstart, y: marking.ystart }, marking);
                return;
            }
            this.update(marking, { x: marking.xstart, y: marking.ystart });
        },
        overlayItem(item) {
            if (!item) {
                this.instance?.removeCustomOverlayItem(this.overlayId);
            }
        },
        moveable(val, prev) {
            if (prev) prev.destroy();
            this.isDragging = false;
            this.isResizing = false;
        },
    },
    mounted() {
        if (this.instance) this.init(this.instance);
    },
    beforeUnmount() {
        this.cleanup(this.instance);
    },
    methods: {
        init(instance: Instance) {
            instance.contentWindow.addEventListener('resize', this.updateMoveable);
            instance.addEventListener('viewState.change', this.updateMoveable);
            instance.addEventListener('page.press', this.onPagePress);
            window.addEventListener('touchmove', this.onTouchmove);
            window.addEventListener('touchend', this.onTouchend);

            if (this.marking) {
                this.create(this.marking.page, { x: this.marking.xstart, y: this.marking.ystart }, this.marking);
            }
        },
        async create(page: number, pos: Point2D, size: Size = { width: 128, height: 80 }) {
            if (!this.instance) throw new Error('Viewer instance not defined');

            const pageIndex = page - 1;
            const spreadEl = this.getSpreadElement(pageIndex);
            if (!spreadEl) return;

            const zoom = this.instance.currentZoomLevel;
            const adjustedSize = {
                width: size.width / zoom,
                height: size.height / zoom,
            };

            const pageInfo = this.instance.pageInfoForIndex(pageIndex)!;

            const target = this.createMarkingEl();
            this.setSize(adjustedSize, target);

            // adjust position so that the marking does not overflow the page and is centered around the point
            const point = new this.PSPDFKit.Geometry.Point({
                x: Math.max(0, Math.min(pos.x - adjustedSize.width / 2, pageInfo.width - adjustedSize.width)),
                y: Math.max(0, Math.min(pos.y - adjustedSize.height / 2, pageInfo.height - adjustedSize.height)),
            });

            this.overlayItem = new this.PSPDFKit.CustomOverlayItem({
                id: this.overlayId,
                node: target,
                pageIndex,
                position: point,
            });
            this.instance.setCustomOverlayItem(this.overlayItem);

            await this.$nextTick();

            this.moveable = new Moveable(spreadEl, {
                origin: false,
                ables: [this.createCtaAble()],
                snappable: true,
                scalable: true,
                draggable: true,
                resizable: true,
                throttleDrag: 0,
                throttleResize: 0,
                target,
                useMutationObserver: true,
                useResizeObserver: true,
                preventClickEventOnDrag: true,
                pinchOutside: false,
                bounds: {
                    position: 'css',
                    left: 0,
                    top: 0,
                    right: 0,
                    bottom: 0,
                },
                props: {
                    // enables the cta Able
                    cta: true,
                },
            })
                .on('drag', this.onChange)
                .on('resize', this.onChange)
                .on('dragStart', () => {
                    this.isDragging = true;
                })
                .on('dragEnd', () => {
                    setTimeout(() => {
                        this.isDragging = false;
                    }, 100);
                })
                .on('resizeStart', () => {
                    this.isResizing = true;
                })
                .on('resizeEnd', () => {
                    setTimeout(() => {
                        this.isResizing = false;
                    }, 100);
                });

            this.$emit('update:marking', {
                page,
                xstart: point.x,
                ystart: point.y,
                width: adjustedSize.width,
                height: adjustedSize.height,
                page_width: pageInfo.width,
                page_height: pageInfo.height,
            });
        },
        createMarkingEl() {
            const el = document.createElement('div');
            el.className = 'moveable-marking hidden';

            // eslint-disable-next-line tipsi/remove-event-listener -- the event listeners are destroyed when the marking is removed
            el.addEventListener('pointerup', this.preventPress);

            return el;
        },
        preventPress(e: Event) {
            e.stopImmediatePropagation();
            e.preventDefault();
        },
        createCtaAble() {
            return {
                name: 'cta',
                render: (moveable, { createElement }) => {
                    const rect = moveable.getRect();
                    // if there is not enough space for the button, we want to display it above the moveable area
                    const pos =
                        moveable.state.containerClientRect.clientHeight - 64 > moveable.state.bottom ? 'top' : 'bottom';

                    return createElement(
                        'button',
                        {
                            key: 'cta',
                            className: 'moveable-marking-btn',
                            style: {
                                left: `${rect.width / 2}px`,
                                [pos]: `${pos === 'top' ? rect.height : 0}px`,
                            },
                            onclick: (e: MouseEvent) => {
                                this.$emit('click-cta');
                                this.preventPress(e);
                            },
                        },
                        [
                            this.$t('document:show.label_ask_question_marking'),
                        ],
                    ) as HTMLElement;
                },
            } as Able;
        },
        update(size?: Size, pos?: Point2D) {
            if (!this.overlayItem) throw new Error('Overlay item does not exist');

            if (pos && (this.overlayItem.position.x !== pos.x || this.overlayItem.position.y !== pos.y)) {
                this.overlayItem = this.overlayItem!.set('position', new this.PSPDFKit.Geometry.Point(pos));
                this.instance?.setCustomOverlayItem(this.overlayItem);
            }

            if (size) {
                const node = this.overlayItem!.node as HTMLElement;
                this.setSize(size, node);
            }
        },
        setSize(size: Size, target: HTMLElement) {
            /* eslint-disable no-param-reassign */
            target.style.width = `${size.width}px`;
            target.style.height = `${size.height}px`;
            /* eslint-enable no-param-reassign */
        },
        reset() {
            this.overlayItem = undefined;
            this.moveable = undefined;
        },
        remove() {
            this.reset();
            this.$emit('update:marking', undefined);
        },
        updateMoveable() {
            window.requestAnimationFrame(() => {
                // update the moveable once the latest changes have been rendered
                this.moveable?.updateRect();
            });
        },
        getClientEventCoordinates(e: MouseEvent | TouchEvent) {
            if (typeof TouchEvent !== 'undefined' && e instanceof TouchEvent) {
                const touch = e.changedTouches[0];
                return { x: touch.clientX, y: touch.clientY };
            }
            return { x: (e as MouseEvent).clientX, y: (e as MouseEvent).clientY };
        },
        onPagePress(event: PressEvent) {
            if (this.isResizing || this.isDragging || this.isTouchmove) return;
            const nativeEvent = event.nativeEvent as MouseEvent | TouchEvent;

            if (
                typeof TouchEvent !== 'undefined' &&
                nativeEvent instanceof TouchEvent &&
                (nativeEvent.touches.length > 0 || nativeEvent.changedTouches.length > 1)
            ) {
                // ignore multiple touches
                return;
            }

            if (this.overlayItem) {
                this.remove();
                return;
            }

            this.create(
                event.pageIndex + 1,
                this.transformScreenToPageSpace!(this.getClientEventCoordinates(nativeEvent), event.pageIndex),
            );
        },
        onTouchmove() {
            this.isTouchmove = true;
        },
        onTouchend() {
            setTimeout(() => {
                this.isTouchmove = false;
            }, 100);
        },
        onChange(data: OnDrag | OnResize) {
            if (!this.overlayItem) return;

            const delta = 'drag' in data ? data.drag.delta : data.delta;
            const pos = this.overlayItem.position;
            const x = pos.x + delta[0];
            const y = pos.y + delta[1];

            this.update(data, { x, y });

            this.$emit('update:marking', {
                ...this.marking,
                xstart: x,
                ystart: y,
                width: data.width,
                height: data.height,
            });
        },
        cleanup(instance: Instance | null | undefined) {
            this.reset();
            instance?.contentWindow?.removeEventListener('resize', this.updateMoveable);
            instance?.removeEventListener('viewState.change', this.updateMoveable);
            instance?.removeEventListener('page.press', this.onPagePress);
            window.removeEventListener('touchmove', this.onTouchmove);
            window.removeEventListener('touchend', this.onTouchend);
        },
        getSpreadElement(pageIndex: number) {
            return this.instance?.contentDocument?.querySelector<HTMLElement>(
                `.PSPDFKit-Spread[data-spread-index="${pageIndex}"]`,
            );
        },
    },
    render: () => '',
});
</script>
